import { Fragment, MouseEvent } from 'react'
import cx from 'classnames'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { kebabCase } from 'lodash'

import { Group } from 'lib/_types'
import { Link } from 'features/i18n/router'
import { fromNowToFullDate } from 'lib/time'
import {
  groupToGroupContextSchema,
  logEvent,
  snowplowEvent,
} from 'lib/analytics'
import { abbreviateNumber } from 'lib/helpers'
import { ROUTE_PATH } from 'lib/paths'
import { useLinger } from 'hooks'
import { selectIsLoggedIn } from 'store/selectors'

import Pinned from 'components/Pinned'
import GroupImage from 'features/Group/GroupImage'
import Avatar from '../Avatar'
import { GroupName } from './components/GroupName'
import GroupCategoryBadge from 'features/Group/components/GroupCategoryBadge'
import GroupTools from './GroupTools'
import CheckBox from 'components/Form/CheckBox'
import { HighlightText } from 'components'

export type GroupItem = {
  index: number
  group?: Group
  enableViewportCheck?: boolean
  userId?: number
  withSelect?: boolean
  compact?: boolean
  showPinnedGroups?: boolean
  smallDetailedSettings?: boolean
  showViewCount?: boolean
  showMenu?: boolean
  descriptionMaxLine?: number
  onClick: (
    group: Group,
    index: number
  ) => (e: MouseEvent<HTMLAnchorElement>) => void
  onGroupToolsMenuActionCallback?: Function
  onSelect?: Function
  selected?: any
  controlMenu?: Function
  highlightText?: string[]
}

export const GroupItem = ({
  index,
  group,
  enableViewportCheck,
  userId,
  withSelect,
  compact,
  showPinnedGroups,
  smallDetailedSettings,
  showViewCount = true,
  showMenu = true,
  descriptionMaxLine = 10,
  onClick,
  onGroupToolsMenuActionCallback,
  onSelect = () => {},
  selected = {},
  controlMenu,
  highlightText,
}: GroupItem) => {
  const { t } = useTranslation()

  const isLoggedIn = useSelector(selectIsLoggedIn)

  const { inViewRef } = useLinger({
    skip: !enableViewportCheck,
    enterCallback: (params) => {
      if (params.first_impression) {
        snowplowEvent.impression({
          group: groupToGroupContextSchema({
            group,
            currentUserId: userId,
          }),
        })
      }
    },
    leaveCallback: (params) => {
      snowplowEvent.linger({
        params,
        group: groupToGroupContextSchema({
          group,
          currentUserId: userId,
        }),
      })
    },
  })

  const isOwnerDead = group?.owner === null

  return (
    <div
      className={cx('GroupsList__item', {
        'GroupsList__item--selectable': withSelect,
        'GroupsList__item--compact': compact,
      })}
      key={group?.id}
      ref={inViewRef}
    >
      {!withSelect && (
        <Link
          className="GroupsList__item__handle"
          to={ROUTE_PATH.GROUP(group?.id)}
          onClick={onClick(group, index)}
        />
      )}

      {showPinnedGroups && group._pinned && (
        <Pinned text={t('group_list.item.pinned')} />
      )}

      <div
        className={cx('GroupsList__item__image', {
          'GroupsList__item__image--private': group?.is_private,
        })}
      >
        <GroupImage
          className="GroupsList__item__image__img"
          group={group}
          placeholderImageTrans
          fallbackToGroupCover
        />

        <div className="GroupsList__item__image__owner">
          <Link
            onClick={() =>
              !isOwnerDead &&
              logEvent.user_profile_open({
                target_user_id: group?.owner.id.toString(),
                from: 'undefined',
              })
            }
            to={
              isOwnerDead ? ROUTE_PATH.HOME : ROUTE_PATH.USER(group?.owner?.id)
            }
          >
            <Avatar
              className="GroupsList__item__image__owner__img"
              user={
                !isOwnerDead
                  ? group?.owner
                  : {
                      profile_icon_thumbnail: '/images/img-dead-owner.jpeg',
                    }
              }
            />
          </Link>
        </div>
      </div>

      <div className="GroupsList__item__info">
        <dt className="GroupsList__item__info__dt">
          <GroupName group={group} onClick={onClick(group, index)} />

          {group?.updated_at && (
            <time
              className="GroupsList__item__info__dt__time"
              title={dayjs(group?.updated_at * 1000).format()}
            >
              {fromNowToFullDate(group?.updated_at * 1000)}
            </time>
          )}
        </dt>

        {(group?.description || group?.unread_counts > 0) && (
          <div className="GroupsList__item__info__content">
            <dd
              className={cx('GroupsList__item__info__dd', {
                'max-line': true,
                [`max-line--${descriptionMaxLine}`]: true,
              })}
            >
              <HighlightText keywords={highlightText}>
                {group?.description}
              </HighlightText>
            </dd>

            {group?.unread_counts > 0 && (
              <span className="GroupsList__item__info__unread-count">
                {group?.unread_counts}
              </span>
            )}
          </div>
        )}

        <div className="GroupsList__item__info__footer">
          <div
            className={cx('GroupsList__item__info__footer__badge-area', {
              'GroupsList__item__info__footer__badge-area--small':
                smallDetailedSettings,
            })}
          >
            {group?.group_category_id ? (
              <GroupCategoryBadge categoryId={group.group_category_id} />
            ) : (
              <span />
            )}

            {!compact && (
              <div
                className={cx(
                  'GroupsList__item__info__footer__badge-area__settings',
                  {
                    'GroupsList__item__info__footer__badge-area__settings--small':
                      smallDetailedSettings,
                  }
                )}
              >
                {[
                  {
                    key: 'only_verified_age',
                    tKey: 'group_info_page.group_info_settings_section.group_info_settings_age_verified',
                  },
                  {
                    key: 'only_mobile_verified',
                    tKey: 'group_info_page.group_info_settings_section.group_settings_phone_verified_only',
                  },
                ]?.map?.((x, i) => {
                  if (!group?.[x?.key]) {
                    return null
                  }

                  return (
                    <div
                      className="GroupsList__item__info__footer__badge-area__settings__item"
                      key={i}
                    >
                      <img
                        className="GroupsList__item__info__footer__badge-area__settings__item__img"
                        src={`/images/groupslist-item-info-footer-badge-area-settings-item-img-${kebabCase(
                          x?.key
                        )}.svg`}
                      />

                      <span className="GroupsList__item__info__footer__badge-area__settings__item__span">
                        {t(x?.tKey)}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className="GroupsList__item__info__footer__action-wrapper">
            <ul className="GroupsList__item__info__ul">
              <li className="GroupsList__item__info__ul__li GroupsList__item__info__ul__li--members">
                {abbreviateNumber(group?.groups_users_count ?? 0)}{' '}
                {t('common.people')}
              </li>

              {!compact && (
                <Fragment>
                  <li className="GroupsList__item__info__ul__li GroupsList__item__info__ul__li--posts">
                    {abbreviateNumber(group?.posts_count ?? 0)}{' '}
                    {t('common.post')}
                  </li>

                  {showViewCount && (
                    <li className="GroupsList__item__info__ul__li GroupsList__item__info__ul__li--view">
                      {abbreviateNumber(group?.views_count ?? 0)}
                    </li>
                  )}
                </Fragment>
              )}
            </ul>

            {isLoggedIn && !withSelect && !compact && showMenu && (
              <GroupTools
                group={group}
                controlMenu={controlMenu}
                onGroupToolsMenuActionCallback={onGroupToolsMenuActionCallback}
              />
            )}
          </div>
        </div>
      </div>

      {withSelect && (
        <CheckBox
          value={selected}
          onChange={(e) => onSelect(group.id, e.target.checked)}
        />
      )}
    </div>
  )
}
